import { useState, useEffect } from 'react';
import { X, Shield, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useLanguage } from '@/hooks/useLanguage';
import { getConsentTranslation } from '@/i18n/consent-translations';
import { consentConfig } from '@/lib/tracking/consent-config';
import {
  loadConsentState,
  saveConsentState,
  ConsentPreferences,
  initializeConsentMode,
} from '@/lib/tracking/consent-store';
import { loadTrackingScripts } from '@/lib/tracking/script-loader';
import { initializeAttribution } from '@/lib/tracking/utm-manager';
import PreferencesModal from './PreferencesModal';

const ConsentBanner = () => {
  const { language } = useLanguage();
  const t = getConsentTranslation(language);
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Initialize consent mode with denied state
    initializeConsentMode();
    
    // Capture UTMs on load
    initializeAttribution();
    
    // Check if we need to show banner
    const state = loadConsentState();
    if (!state.hasConsented) {
      // Delay showing banner slightly for better UX
      setTimeout(() => setIsVisible(true), 1000);
    } else {
      // Already consented, load appropriate scripts
      loadTrackingScripts(state.preferences);
    }
  }, []);

  const handleAcceptAll = async () => {
    const preferences: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    
    await saveConsentState(preferences, language);
    loadTrackingScripts(preferences);
    setIsVisible(false);
  };

  const handleReject = async () => {
    const preferences: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    };
    
    await saveConsentState(preferences, language);
    setIsVisible(false);
  };

  const handleSavePreferences = async (preferences: ConsentPreferences) => {
    await saveConsentState(preferences, language);
    loadTrackingScripts(preferences);
    setShowModal(false);
    setIsVisible(false);
  };

  if (!isVisible && !showModal) return null;

  const policyUrls = consentConfig.policyUrls;
  const privacyUrl = policyUrls.privacy[language] || policyUrls.privacy.en;
  const cookieUrl = policyUrls.cookies[language] || policyUrls.cookies.en;

  return (
    <>
      {/* Main Banner */}
      {isVisible && !showModal && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 animate-in slide-in-from-bottom-4 duration-500"
          role="dialog"
          aria-label="Cookie consent"
          aria-modal="false"
        >
          <Card className="max-w-4xl mx-auto bg-background/95 backdrop-blur-lg border-border shadow-2xl">
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h2 className="text-lg font-semibold text-foreground">
                      {t.banner.title}
                    </h2>
                    <button 
                      onClick={handleReject}
                      className="sm:hidden text-muted-foreground hover:text-foreground p-1"
                      aria-label="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {t.banner.description}
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-4">
                    <a 
                      href={privacyUrl}
                      className="hover:text-primary underline underline-offset-2"
                    >
                      {t.banner.privacyPolicy}
                    </a>
                    <span>•</span>
                    <a 
                      href={cookieUrl}
                      className="hover:text-primary underline underline-offset-2"
                    >
                      {t.banner.cookiePolicy}
                    </a>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="w-full sm:w-auto"
                      size="lg"
                    >
                      {t.banner.acceptAll}
                    </Button>
                    
                    <Button
                      onClick={handleReject}
                      variant="outline"
                      className="w-full sm:w-auto"
                      size="lg"
                    >
                      {t.banner.reject}
                    </Button>
                    
                    <Button
                      onClick={() => setShowModal(true)}
                      variant="ghost"
                      className="w-full sm:w-auto"
                      size="lg"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      {t.banner.customize}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Preferences Modal */}
      <PreferencesModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          if (!loadConsentState().hasConsented) {
            setIsVisible(true);
          }
        }}
        onSave={handleSavePreferences}
      />
    </>
  );
};

export default ConsentBanner;
